import { useEffect, useState } from 'react';
import { Settings as SettingsIcon } from 'lucide-react';
import {
  getReconSettings,
  saveReconSettings,
  QUICK_THRESHOLDS,
} from './reconSettings';
import HelpTip from './HelpTip';
import MfaSettings from './MfaSettings';

export default function Settings({ onNavigate }) {
  const [settings, setSettings] = useState(getReconSettings);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    setSettings(getReconSettings());
  }, []);

  const update = (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    saveReconSettings(patch);
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 2000);
  };

  return (
    <div className="space-y-6">
      <header className="page-hero">
        <p className="section-title">Configuration</p>
        <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-100">Settings</h2>
        <p className="mt-2 max-w-2xl text-sm text-slate-400">
          Default pay period and match tolerances for <strong className="text-slate-300">Payroll Recon</strong>.
          Changes are saved automatically and apply to the next reconciliation run.
        </p>
        {savedFlash && (
          <p className="mt-3 text-sm text-emerald-400">Settings saved.</p>
        )}
      </header>

      <div className="card-panel max-w-2xl p-5">
        <div className="mb-4 flex items-center gap-2">
          <SettingsIcon className="h-5 w-5 text-cyan-500" />
          <h3 className="font-semibold text-slate-100">
            Reconciliation thresholds <HelpTip term="tolerance" />
          </h3>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="font-medium text-slate-300">Pay period</span>
            <input
              value={settings.payPeriod}
              onChange={(e) => update({ payPeriod: e.target.value })}
              className="input-field font-mono"
              placeholder="202610"
            />
            <span className="mt-1 block text-xs text-slate-500">
              ADP pay period / cycle (e.g. 202610). Add and close cycles on{' '}
              <button type="button" className="link-accent" onClick={() => onNavigate?.('periods')}>
                Payroll periods
              </button>
              .
            </span>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-300">Max difference (hours)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={settings.hoursToleranceAmount}
              onChange={(e) => update({ hoursToleranceAmount: e.target.value })}
              className="input-field font-mono"
            />
            <span className="mt-1 block text-xs text-slate-500">Used for time reconciliation scope</span>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-300">Max difference ($)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={settings.toleranceAmount}
              onChange={(e) => update({ toleranceAmount: e.target.value })}
              className="input-field font-mono"
            />
            <span className="mt-1 block text-xs text-slate-500">Absolute dollar variance allowed</span>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-300">Max difference (%)</span>
            <input
              type="number"
              min="0"
              step="0.1"
              value={settings.tolerancePercent}
              onChange={(e) => update({ tolerancePercent: e.target.value })}
              placeholder="0"
              className="input-field font-mono"
            />
            <span className="mt-1 block text-xs text-slate-500">Optional percent tolerance (0 = dollars only)</span>
          </label>
        </div>

        <div className="mt-5">
          <span className="text-xs font-medium text-slate-500">Quick thresholds</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {QUICK_THRESHOLDS.map(({ label, amount, percent }) => (
              <button
                key={label}
                type="button"
                onClick={() => update({ toleranceAmount: amount, tolerancePercent: percent })}
                className="rounded-full border border-slate-600 bg-slate-800 px-3 py-1 text-xs font-medium text-slate-300 hover:border-cyan-600/50 hover:bg-slate-700"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <MfaSettings />
    </div>
  );
}
